import React, { useEffect, useRef, useState } from 'react';

import { Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { setReportPassword } from 'redux/actions/reportActions/Actions';
import { ReportState } from 'redux/reducers/ReportReducer';

import { MenuNode } from 'models/MenuNode';
import { User } from 'models/User';

import { MenuService } from 'services/MenuService';

import { useThemeLayoutConfig } from 'config/ThemeDashboardLayoutConfig';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../components/Layout/Layout';
import { MAIN_BACKEND_URL, MenuType } from '../../config/Config';
import PasswordOverlay from '../PowerBiReport/components/PasswordOverlay';
import FullscreenButton from './components/FullscreenButton';
import ViewerContent from './components/ViewerContent';

import './DynamicViewer.scss';

const PASSWORD_SESSION_DURATION = 30 * 60 * 1000; // 30 minutos en ms

function DynamicViewer() {
  const { key }: any = useParams();
  const dispatch = useDispatch();
  const { theme } = useThemeLayoutConfig();
  const user: User = useSelector((state: any) => state.auth);
  const { passwords }: ReportState = useSelector((state: any) => state.report);
  const { i18n, t } = useTranslation();
  const [src, setSrc] = useState('');
  const [title, setTitle] = useState('');
  const [enableFullscreen, setEnableFullscreen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [validatingPassword, setValidatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const reportKey = typeof key === 'string' ? key : '';
  const storedData = reportKey ? passwords[reportKey] : undefined;
  const isAuthenticated =
    !!storedData &&
    Date.now() - storedData.timestamp < PASSWORD_SESSION_DURATION;

  // Derivamos los datos del menú de forma síncrona para evitar parpadeos (flicker)
  const menuData = React.useMemo(() => {
    return FunctionsHelper.buildBreadcrumbForCode(
      (user?.menus as MenuNode[]) || [],
      key,
      {
        allowedTypes: [
          MenuType.IFRAME_VIEWER,
          MenuType.PDF_VIEWER,
          MenuType.OFFICE_VIEWER,
        ],
        includeAllParents: false,
        includeHome: false,
        showLeafIcon: true,
      }
    );
  }, [key, user?.menus]);

  const availableData = menuData
    ? menuData.chain[menuData.chain.length - 1]
    : null;
  const isEncrypted = !!availableData?.additionalConfig?.isPasswordProtected;

  // El overlay solo se muestra si el reporte requiere contraseña Y el usuario NO está autenticado
  const showPasswordOverlay = isEncrypted && !isAuthenticated;

  /* Settings */
  const heightOffset = 164 + 36;
  const heightPadding = 48;
  const vh = window.innerHeight;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullscreenChange
      );
    };
  }, []);

  // Reset completo de todos los estados cuando cambia el key del reporte
  useEffect(() => {
    setSrc('');
    setTitle('');
    setEnableFullscreen(false);
    setValidatingPassword(false);
    setPasswordError(null);
    setIframeLoaded(false);
  }, [key]);

  useEffect(() => {
    init();
  }, [key, user, i18n.language, isAuthenticated]);

  const init = async (forceAuthenticated?: boolean) => {
    if (availableData) {
      const isActuallyAuthenticated = forceAuthenticated ?? isAuthenticated;

      if (isEncrypted && !isActuallyAuthenticated) {
        setTitle(menuData?.title || availableData.title);
        return;
      }

      setEnableFullscreen(
        [MenuType.IFRAME_VIEWER, MenuType.PDF_VIEWER].includes(
          availableData.menuTypeId as any
        )
      );

      if (availableData.menuTypeId === MenuType.IFRAME_VIEWER) {
        const url = availableData.redirectLink || '';
        const prefixUrl = availableData?.additionalConfig?.prefixUrl || '';
        const suffixUrl = availableData?.additionalConfig?.suffixUrl || '';
        setSrc(`${prefixUrl}${url}${suffixUrl}`);
      } else if (availableData.menuTypeId === MenuType.PDF_VIEWER) {
        const s3Key = availableData?.additionalConfig?.file || '';
        const rawUrl = `${MAIN_BACKEND_URL}/file/get?key=${s3Key}`;
        setSrc(rawUrl);
      } else {
        // Office
        const s3Key = availableData?.additionalConfig?.file || '';
        const rawUrl = `${MAIN_BACKEND_URL}/file/get?key=${s3Key}`;
        setSrc(`https://view.officeapps.live.com/op/view.aspx?src=${rawUrl}`);
      }

      setTitle(menuData?.title || availableData.title);
    } else {
      setSrc('');
      setTitle('');
    }
  };

  const handlePasswordValidation = async (values: { password: string }) => {
    setValidatingPassword(true);
    setPasswordError(null);

    const response = await MenuService.validateEncryptedReportSecret(key, {
      secret: values.password,
    });

    if (
      response.success &&
      response.data &&
      'validated' in response.data &&
      (response.data as { validated: boolean }).validated
    ) {
      setPasswordError(null);
      dispatch(setReportPassword(reportKey, values.password));
      // No llamamos a init() aquí manualmente.
      // El dispatch actualizará isAuthenticated, lo que disparará el useEffect que llama a init().
    } else {
      setPasswordError(
        t('scenes.power_bi_report.password_modal.invalid_password_simple')
      );
    }

    setValidatingPassword(false);
  };

  const handleFullscreen = () => {
    const container = containerRef.current;
    if (!enableFullscreen || !container) return;
    if (!isFullscreen) {
      container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <Layout title={title}>
      <div
        className="dynamic-viewer-wrapper"
        style={
          {
            '--report-primary-color': theme?.token?.colorPrimary,
          } as React.CSSProperties
        }
      >
        {showPasswordOverlay && (
          <PasswordOverlay
            key={key}
            validatingPassword={validatingPassword}
            passwordError={passwordError}
            onValidate={handlePasswordValidation}
          />
        )}

        <Card
          style={{
            width: '100%',
            height: isFullscreen ? '100vh' : `${vh - heightOffset}px`,
            position: 'relative',
          }}
          className="dynamic-viewer-card-container"
        >
          <ViewerContent
            src={src}
            title={title}
            reportKey={reportKey}
            iframeLoaded={iframeLoaded}
            onIframeLoad={() => setIframeLoaded(true)}
            isFullscreen={isFullscreen}
            vh={vh}
            heightOffset={heightOffset}
            heightPadding={heightPadding}
            containerRef={containerRef}
          />

          {enableFullscreen && src && iframeLoaded && (
            <FullscreenButton
              isFullscreen={isFullscreen}
              onToggle={handleFullscreen}
            />
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default DynamicViewer;
